import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { HiChevronRight, HiChevronDown } from 'react-icons/hi';

const Hero = () => {
  const handleScrollToNext = () => {
    const nextSection = document.querySelector('#problem-section');
    if (nextSection) {
      nextSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section style={{ backgroundColor: '#F5F0E8', paddingTop: '120px', paddingBottom: '80px' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1.5rem' }}>
        {/* Hero Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr',
          gap: '2rem',
          alignItems: 'start',
        }} className="hero-grid">
          {/* Left: Big heading */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
          >
            <h1 style={{
              fontSize: 'clamp(2.5rem, 5.5vw, 4.5rem)',
              fontWeight: 700,
              lineHeight: 1.08,
              letterSpacing: '-0.03em',
              color: '#1A1A1A',
              fontFamily: '"DM Sans", sans-serif',
            }}>
              AI{' '}
              <span style={{
                textDecoration: 'underline',
                textDecorationThickness: '3px',
                textUnderlineOffset: '6px',
              }}>
                automation
              </span>
              {' '}and products that help Indian businesses scale
            </h1>
          </motion.div>

          {/* Right: Description */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: 'easeOut', delay: 0.15 }}
          >
            <p style={{
              fontSize: '1.1rem',
              lineHeight: 1.7,
              color: '#666666',
              maxWidth: '480px',
              fontFamily: '"DM Sans", sans-serif',
            }}>
              Rishant Technologies builds AI-powered WhatsApp bots, lead systems, and customer automation — designed for Indian businesses that are serious about growth.
            </p>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginTop: '2rem',
            }}>
              <Link
                to="/contact"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '14px 28px',
                  backgroundColor: '#1A1A1A',
                  color: '#FFFFFF',
                  borderRadius: '999px',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  textDecoration: 'none',
                  transition: 'background-color 0.15s',
                  fontFamily: '"DM Sans", sans-serif',
                }}
              >
                Book Your Free Audit <HiChevronRight size={18} />
              </Link>
              <button
                onClick={handleScrollToNext}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '14px 28px',
                  backgroundColor: 'transparent',
                  color: '#1A1A1A',
                  border: '1.5px solid #E0D8CC',
                  borderRadius: '999px',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  fontFamily: '"DM Sans", sans-serif',
                }}
              >
                See How It Works
              </button>
            </div>
          </motion.div>
        </div>

        {/* Featured Dark Card */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: 'easeOut', delay: 0.3 }}
          style={{
            marginTop: '4rem',
            borderRadius: '20px',
            backgroundColor: '#111111',
            color: '#FFFFFF',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <div style={{ padding: 'clamp(2rem, 4vw, 3.5rem)' }}>
            <h2 style={{
              fontSize: 'clamp(1.75rem, 3vw, 2.5rem)',
              fontWeight: 700,
              lineHeight: 1.15,
              letterSpacing: '-0.02em',
              marginBottom: '1rem',
              color: '#FFFFFF',
              fontFamily: '"DM Sans", sans-serif',
            }}>
              WhatsApp Business Automation
            </h2>
            <p style={{
              fontSize: '1rem',
              lineHeight: 1.65,
              color: 'rgba(255,255,255,0.5)',
              maxWidth: '520px',
              marginBottom: '1.5rem',
              fontFamily: '"DM Sans", sans-serif',
            }}>
              24/7 automated replies, lead capture, appointment booking, payment reminders — your customers never wait, your staff focus on real work.
            </p>
            <Link
              to="/services"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 24px',
                backgroundColor: '#FFFFFF',
                color: '#111111',
                borderRadius: '999px',
                fontSize: '0.9rem',
                fontWeight: 600,
                textDecoration: 'none',
                fontFamily: '"DM Sans", sans-serif',
              }}
            >
              Learn more <HiChevronRight size={16} />
            </Link>
          </div>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 0.6 }}
          style={{ textAlign: 'center', marginTop: '3rem' }}
        >
          <button
            onClick={handleScrollToNext}
            style={{
              background: 'none',
              border: 'none',
              color: '#999999',
              cursor: 'pointer',
              display: 'inline-flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '4px',
              fontSize: '0.8rem',
              fontWeight: 500,
              fontFamily: '"DM Sans", sans-serif',
            }}
            aria-label="Scroll down"
          >
            Scroll
            <motion.div
              animate={{ y: [0, 6, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
            >
              <HiChevronDown size={20} />
            </motion.div>
          </button>
        </motion.div>
      </div>

      {/* CSS for responsive grid */}
      <style>{`
        @media (min-width: 768px) {
          .hero-grid {
            grid-template-columns: 1.2fr 1fr !important;
            gap: 4rem !important;
            align-items: start !important;
          }
        }
      `}</style>
    </section>
  );
};

export default Hero;
